import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import apiService from '../services/apiService';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
  isBefore,
  startOfDay,
  getDay,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, ArrowLeft, Pencil, Trash2, Plus, X, SlidersHorizontal } from 'lucide-react';

/* ──────────────────────────────────────────────────────────────
   CONFIG
───────────────────────────────────────────────────────────────*/
const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&display=swap';

const PANEL_HEIGHT = 380;

const SERVICE_TYPES = [
  'Bakım', 'Arıza', 'Kaporta', 'HTA', 'Genel Danışma', 'Fiyat / Paket / Garanti',
];

const DEFAULT_CHIP_COUNT = 3;

// Mon→Sun labels and their getDay() values (0=Sun,1=Mon…)
const WEEKDAY_LABELS  = ['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'];
const WEEKDAY_GETDAY  = [1, 2, 3, 4, 5, 6, 0];

const PRESET_COLORS = [
  '#4a9e8a', '#34d399', '#a78bfa', '#fb923c',
  '#f472b6', '#60a5fa', '#facc15', '#f87171',
];

/* ──────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────*/
function timeStrToMinutes(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTimeStr(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const DURATION_OPTIONS = [30, 60, 90, 120];
const DEFAULT_DURATION_MINUTES = 60;

function getApptRange(appt) {
  const start = timeStrToMinutes(appt.time);
  const dur = appt.durationMinutes || DEFAULT_DURATION_MINUTES;
  return { start, end: start + dur };
}

function rangesOverlap(a, b) {
  return a.start < b.end && b.start < a.end;
}

function getHourOverlap(hour, range) {
  const hourStart = hour * 60;
  const hourEnd = (hour + 1) * 60;
  const overlapStart = Math.max(range.start, hourStart);
  const overlapEnd = Math.min(range.end, hourEnd);
  if (overlapStart >= overlapEnd) return null;
  return {
    topPercent: ((overlapStart - hourStart) / 60) * 100,
    heightPercent: ((overlapEnd - overlapStart) / 60) * 100,
    isStart: range.start >= hourStart && range.start < hourEnd,
  };
}

function generateSlotTimes(workingHours, slotDurationMinutes) {
  const slots = [];
  let min = timeStrToMinutes(workingHours.start);
  const end = timeStrToMinutes(workingHours.end);
  while (min < end) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    min += slotDurationMinutes;
  }
  return slots;
}

function getTimelineHours(workingHours) {
  const startH = Math.floor(timeStrToMinutes(workingHours.start) / 60);
  const endH   = Math.ceil(timeStrToMinutes(workingHours.end) / 60);
  const hours  = [];
  for (let h = startH; h < endH; h++) hours.push(h);
  return hours;
}

function heatColors(ratio, isLight = false) {
  const r   = Math.max(0, Math.min(1, ratio));
  const hue = Math.round(142 * (1 - r));
  if (isLight) {
    return {
      bg:     `hsl(${hue}, ${Math.round(50 + 15 * r)}%, ${Math.round(94 - 8 * r)}%)`,
      border: `hsl(${hue}, ${Math.round(45 + 20 * r)}%, ${Math.round(65 - 10 * r)}%)`,
      badge:  `hsl(${hue}, 65%, 30%)`,
    };
  }
  return {
    bg:     `hsl(${hue}, ${Math.round(55 + 20 * r)}%, ${Math.round(11 + 5 * r)}%)`,
    border: `hsl(${hue}, ${Math.round(55 + 25 * r)}%, ${Math.round(38 + 12 * r)}%)`,
    badge:  `hsl(${hue}, 80%, 65%)`,
  };
}

function makeCalendarStyles(isLight) {
  const t = isLight ? {
    rootColor:           '#0f172a',
    monthLabelColor:     '#0f172a',
    navBtnBorder:        'rgba(15,23,42,0.14)',
    navBtnBg:            'rgba(15,23,42,0.05)',
    navBtnColor:         '#475569',
    navBtnHoverBg:       'rgba(15,23,42,0.09)',
    navBtnHoverColor:    '#0f172a',
    weekdayColor:        '#94a3b8',
    weekdayBorder:       'rgba(15,23,42,0.08)',
    weekdayEditHoverColor: '#1e293b',
    weekdayEditHoverBg:  'rgba(15,23,42,0.07)',
    weekdayEditHoverBdr: 'rgba(15,23,42,0.18)',
    monthCardBg:         'rgba(15,23,42,0.025)',
    monthCardBorder:     'rgba(15,23,42,0.10)',
    dayBorder:           'rgba(15,23,42,0.15)',
    dayBg:               'rgba(15,23,42,0.025)',
    dayHoverBorder:      'rgba(15,23,42,0.30)',
    dayHoverBg:          'rgba(15,23,42,0.055)',
    dayPastBg:           'rgba(15,23,42,0.015)',
    dayPastBorder:       'rgba(15,23,42,0.07)',
    dayPastAfterBg:      'rgba(15,23,42,0.04)',
    dayPastNumColor:     '#94a3b8',
    dayPastBadgeColor:   '#94a3b8',
    heatBarDefault:      'rgba(15,23,42,0.08)',
    dayNumColor:         '#1e293b',
    todayPipBg:          '#4a9e8a',
    todayPipColor:       '#ffffff',
    moreLabelColor:      '#64748b',
    hoverPopupBg:        '#ffffff',
    hoverPopupBorder:    'rgba(15,23,42,0.14)',
    hoverPopupNumColor:  '#64748b',
    dayPanelBg:          '#f4f6fb',
    dayPanelBorder:      'rgba(15,23,42,0.12)',
    panelHeadBg:         '#e8eef6',
    panelHeadColor:      '#0f172a',
    panelBackBorder:     '#d1d5db',
    panelBackBg:         '#e2e8f0',
    panelBackColor:      '#475569',
    panelBackHoverBg:    '#d1d5db',
    panelBackHoverColor: '#1e293b',
    panelMetaColor:      '#64748b',
    hourColBorder:       'rgba(15,23,42,0.08)',
    hourLabelColor:      '#64748b',
    hourLabelBg:         'rgba(15,23,42,0.04)',
    hourLabelBorder:     'rgba(15,23,42,0.08)',
    hourTrackBg:         'rgba(15,23,42,0.02)',
    apptBlockBorder:     'rgba(239,68,68,0.35)',
    apptBlockBg:         'rgba(239,68,68,0.08)',
    apptContBorder:      'rgba(239,68,68,0.20)',
    apptContBg:          'rgba(239,68,68,0.05)',
    apptRoBorder:        'rgba(15,23,42,0.12)',
    apptRoBg:            'rgba(15,23,42,0.04)',
    apptRoBarBg:         '#94a3b8',
    apptRoTextColor:     '#94a3b8',
    apptTimeLblColor:    '#475569',
    apptNameColor:       '#0f172a',
    apptSvcColor:        '#475569',
    apptPlateColor:      '#334155',
    apptActBorder:       'rgba(15,23,42,0.12)',
    apptActBg:           'rgba(15,23,42,0.05)',
    apptActColor:        '#64748b',
    apptActHoverBg:      'rgba(15,23,42,0.10)',
    apptActHoverColor:   '#0f172a',
    hourAddBorder:       'rgba(15,23,42,0.15)',
    hourAddColor:        'rgba(15,23,42,0.25)',
    hourAddSmBorder:     'rgba(15,23,42,0.12)',
    hourAddSmColor:      'rgba(15,23,42,0.25)',
    hourAddSmHoverBdr:   '#94a3b8',
    hourAddSmHoverColor: '#475569',
    modalOverlay:        'rgba(15,23,42,0.35)',
    modalBg:             '#ffffff',
    modalBorder:         'rgba(15,23,42,0.12)',
    modalHeadBg:         '#f0f4f8',
    modalHeadColor:      '#0f172a',
    modalXColor:         '#64748b',
    modalXHoverColor:    '#0f172a',
    fieldLabelColor:     '#64748b',
    fieldBorder:         'rgba(15,23,42,0.14)',
    fieldColor:          '#0f172a',
    fieldBg:             'rgba(15,23,42,0.04)',
    fieldFocusBorder:    '#4a9e8a',
    fieldFocusBg:        'rgba(15,23,42,0.06)',
    fieldOptionBg:       '#ffffff',
    fieldOptionColor:    '#0f172a',
    durationBorder:      'rgba(15,23,42,0.14)',
    durationBg:          'rgba(15,23,42,0.04)',
    durationColor:       '#475569',
    durationHoverBorder: 'rgba(74,158,138,0.5)',
    durationHoverColor:  '#334155',
    durationRangeColor:  '#64748b',
    durationRangeStrong: '#475569',
    btnGhostBg:          'rgba(15,23,42,0.06)',
    btnGhostColor:       '#475569',
    btnGhostBorder:      'rgba(15,23,42,0.12)',
    btnGhostHoverBg:     'rgba(15,23,42,0.10)',
    editBtnBorder:       'rgba(15,23,42,0.12)',
    editBtnBg:           'rgba(15,23,42,0.04)',
    editBtnColor:        '#64748b',
    editBtnHoverBg:      'rgba(15,23,42,0.08)',
    editBtnHoverColor:   '#1e293b',
    editBtnHoverBorder:  'rgba(15,23,42,0.22)',
    editPanelBg:         'rgba(15,23,42,0.025)',
    editPanelBorder:     'rgba(15,23,42,0.10)',
    editPanelTitleColor: '#64748b',
    presetCardBorder:    'rgba(15,23,42,0.12)',
    presetCardBg:        'rgba(15,23,42,0.03)',
    presetCardHoverBg:   'rgba(15,23,42,0.06)',
    presetCardHoverBdr:  'rgba(15,23,42,0.22)',
    presetNameColor:     '#1e293b',
    presetMetaColor:     '#6b7280',
    presetEditColor:     '#6b7280',
    presetEditHoverColor:'#475569',
    presetDelHoverColor: '#ef4444',
    presetNewBorder:     'rgba(15,23,42,0.18)',
    presetNewColor:      '#64748b',
    presetNewHoverBdr:   'rgba(15,23,42,0.32)',
    presetNewHoverColor: '#475569',
    editFieldBorder:     'rgba(15,23,42,0.14)',
    editFieldColor:      '#0f172a',
    editFieldBg:         'rgba(15,23,42,0.04)',
    editHintColor:       '#64748b',
    editNavMonthColor:   '#1e293b',
    editNavBtnBorder:    'rgba(15,23,42,0.12)',
    editNavBtnBg:        'rgba(15,23,42,0.04)',
    editNavBtnColor:     '#64748b',
    editNavBtnHoverBg:   'rgba(15,23,42,0.09)',
    editNavBtnHoverColor:'#1e293b',
    weekdayBtnBorder:    'rgba(15,23,42,0.12)',
    weekdayBtnBg:        'rgba(15,23,42,0.04)',
    weekdayBtnColor:     '#64748b',
    weekdayBtnHoverBg:   'rgba(15,23,42,0.09)',
    weekdayBtnHoverColor:'#1e293b',
    weekdayBtnHoverBdr:  'rgba(15,23,42,0.22)',
    colorSwatchActiveBdr:'#1e293b',
  } : {
    rootColor:           '#f1f5f9',
    monthLabelColor:     '#f1f5f9',
    navBtnBorder:        'rgba(255,255,255,0.1)',
    navBtnBg:            'rgba(255,255,255,0.05)',
    navBtnColor:         '#94a3b8',
    navBtnHoverBg:       'rgba(255,255,255,0.09)',
    navBtnHoverColor:    '#f1f5f9',
    weekdayColor:        '#475569',
    weekdayBorder:       'rgba(255,255,255,0.06)',
    weekdayEditHoverColor: '#e2e8f0',
    weekdayEditHoverBg:  'rgba(255,255,255,0.07)',
    weekdayEditHoverBdr: 'rgba(255,255,255,0.15)',
    monthCardBg:         'rgba(255,255,255,0.03)',
    monthCardBorder:     'rgba(255,255,255,0.08)',
    dayBorder:           'rgba(255,255,255,0.13)',
    dayBg:               'rgba(255,255,255,0.03)',
    dayHoverBorder:      'rgba(255,255,255,0.28)',
    dayHoverBg:          'rgba(255,255,255,0.055)',
    dayPastBg:           'rgba(255,255,255,0.015)',
    dayPastBorder:       'rgba(255,255,255,0.07)',
    dayPastAfterBg:      'rgba(255,255,255,0.06)',
    dayPastNumColor:     '#475569',
    dayPastBadgeColor:   '#475569',
    heatBarDefault:      'rgba(255,255,255,0.08)',
    dayNumColor:         '#e2e8f0',
    todayPipBg:          '#4a9e8a',
    todayPipColor:       '#0c0d11',
    moreLabelColor:      '#475569',
    hoverPopupBg:        '#1e293b',
    hoverPopupBorder:    'rgba(255,255,255,0.12)',
    hoverPopupNumColor:  '#64748b',
    dayPanelBg:          '#111318',
    dayPanelBorder:      'rgba(255,255,255,0.1)',
    panelHeadBg:         '#1a1f2e',
    panelHeadColor:      '#f1f5f9',
    panelBackBorder:     '#334155',
    panelBackBg:         '#232d40',
    panelBackColor:      '#94a3b8',
    panelBackHoverBg:    '#2d3a52',
    panelBackHoverColor: '#e2e8f0',
    panelMetaColor:      '#64748b',
    hourColBorder:       'rgba(255,255,255,0.06)',
    hourLabelColor:      '#64748b',
    hourLabelBg:         'rgba(255,255,255,0.04)',
    hourLabelBorder:     'rgba(255,255,255,0.06)',
    hourTrackBg:         'rgba(255,255,255,0.02)',
    apptBlockBorder:     'rgba(239,68,68,0.35)',
    apptBlockBg:         'rgba(239,68,68,0.10)',
    apptContBorder:      'rgba(239,68,68,0.22)',
    apptContBg:          'rgba(239,68,68,0.07)',
    apptRoBorder:        'rgba(255,255,255,0.1)',
    apptRoBg:            'rgba(255,255,255,0.04)',
    apptRoBarBg:         '#475569',
    apptRoTextColor:     '#475569',
    apptTimeLblColor:    '#94a3b8',
    apptNameColor:       '#f1f5f9',
    apptSvcColor:        '#94a3b8',
    apptPlateColor:      '#cbd5e1',
    apptActBorder:       'rgba(255,255,255,0.1)',
    apptActBg:           'rgba(255,255,255,0.05)',
    apptActColor:        '#64748b',
    apptActHoverBg:      'rgba(255,255,255,0.1)',
    apptActHoverColor:   '#f1f5f9',
    hourAddBorder:       'rgba(255,255,255,0.1)',
    hourAddColor:        'rgba(255,255,255,0.2)',
    hourAddSmBorder:     'rgba(255,255,255,0.1)',
    hourAddSmColor:      'rgba(255,255,255,0.2)',
    hourAddSmHoverBdr:   '#94a3b8',
    hourAddSmHoverColor: '#94a3b8',
    modalOverlay:        'rgba(10,12,20,0.45)',
    modalBg:             '#1e293b',
    modalBorder:         'rgba(255,255,255,0.1)',
    modalHeadBg:         '#1a1f2e',
    modalHeadColor:      '#f1f5f9',
    modalXColor:         '#64748b',
    modalXHoverColor:    '#f1f5f9',
    fieldLabelColor:     '#64748b',
    fieldBorder:         'rgba(255,255,255,0.12)',
    fieldColor:          '#f1f5f9',
    fieldBg:             'rgba(255,255,255,0.05)',
    fieldFocusBorder:    '#4a9e8a',
    fieldFocusBg:        'rgba(255,255,255,0.07)',
    fieldOptionBg:       '#1e293b',
    fieldOptionColor:    '#f1f5f9',
    durationBorder:      'rgba(255,255,255,0.12)',
    durationBg:          'rgba(255,255,255,0.04)',
    durationColor:       '#94a3b8',
    durationHoverBorder: 'rgba(74,158,138,0.5)',
    durationHoverColor:  '#cbd5e1',
    durationRangeColor:  '#64748b',
    durationRangeStrong: '#94a3b8',
    btnGhostBg:          'rgba(255,255,255,0.06)',
    btnGhostColor:       '#94a3b8',
    btnGhostBorder:      'rgba(255,255,255,0.1)',
    btnGhostHoverBg:     'rgba(255,255,255,0.1)',
    editBtnBorder:       'rgba(255,255,255,0.1)',
    editBtnBg:           'rgba(255,255,255,0.04)',
    editBtnColor:        '#64748b',
    editBtnHoverBg:      'rgba(255,255,255,0.08)',
    editBtnHoverColor:   '#e2e8f0',
    editBtnHoverBorder:  'rgba(255,255,255,0.2)',
    editPanelBg:         'rgba(255,255,255,0.03)',
    editPanelBorder:     'rgba(255,255,255,0.1)',
    editPanelTitleColor: '#94a3b8',
    presetCardBorder:    'rgba(255,255,255,0.1)',
    presetCardBg:        'rgba(255,255,255,0.04)',
    presetCardHoverBg:   'rgba(255,255,255,0.07)',
    presetCardHoverBdr:  'rgba(255,255,255,0.2)',
    presetNameColor:     '#e2e8f0',
    presetMetaColor:     '#475569',
    presetEditColor:     '#475569',
    presetEditHoverColor:'#94a3b8',
    presetDelHoverColor: '#f87171',
    presetNewBorder:     'rgba(255,255,255,0.15)',
    presetNewColor:      '#64748b',
    presetNewHoverBdr:   'rgba(255,255,255,0.3)',
    presetNewHoverColor: '#94a3b8',
    editFieldBorder:     'rgba(255,255,255,0.12)',
    editFieldColor:      '#f1f5f9',
    editFieldBg:         'rgba(255,255,255,0.05)',
    editHintColor:       '#64748b',
    editNavMonthColor:   '#e2e8f0',
    editNavBtnBorder:    'rgba(255,255,255,0.1)',
    editNavBtnBg:        'rgba(255,255,255,0.04)',
    editNavBtnColor:     '#64748b',
    editNavBtnHoverBg:   'rgba(255,255,255,0.09)',
    editNavBtnHoverColor:'#e2e8f0',
    weekdayBtnBorder:    'rgba(255,255,255,0.1)',
    weekdayBtnBg:        'rgba(255,255,255,0.04)',
    weekdayBtnColor:     '#64748b',
    weekdayBtnHoverBg:   'rgba(255,255,255,0.09)',
    weekdayBtnHoverColor:'#e2e8f0',
    weekdayBtnHoverBdr:  'rgba(255,255,255,0.2)',
    colorSwatchActiveBdr:'#fff',
  };

  return `
    .sc-root {
      font-family: 'Sora', -apple-system, BlinkMacSystemFont, sans-serif;
      -webkit-font-smoothing: antialiased;
      min-height: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 28px 20px 36px;
      background: transparent;
      color: ${t.rootColor};
    }
    .sc-root *, .sc-root *::before, .sc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

    .sc-wrapper {
      width: 100%;
      max-width: 1080px;
      position: relative;
    }

    .sc-month-card {
      width: 100%;
      background: ${t.monthCardBg};
      border: 1px solid ${t.monthCardBorder};
      border-radius: 16px;
      padding: 24px 26px 28px;
    }

    .sc-month-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
    }
    .sc-month-label {
      font-size: 1.55rem; font-weight: 700;
      color: ${t.monthLabelColor};
      text-transform: capitalize; letter-spacing: -0.03em; line-height: 1;
    }
    .sc-month-nav { display: flex; gap: 5px; }

    .sc-nav-btn {
      display: flex; align-items: center; justify-content: center;
      width: 36px; height: 36px;
      border: 1px solid ${t.navBtnBorder}; border-radius: 9px;
      background: ${t.navBtnBg}; color: ${t.navBtnColor};
      cursor: pointer; padding: 0;
    }
    .sc-nav-btn:hover { background: ${t.navBtnHoverBg}; color: ${t.navBtnHoverColor}; }

    .sc-grid {
      display: grid;
      grid-template-columns: repeat(7, minmax(0, 1fr));
      gap: 8px;
    }
    .sc-weekday {
      text-align: center;
      font-size: 0.72rem; font-weight: 700;
      color: ${t.weekdayColor}; text-transform: uppercase; letter-spacing: 0.07em;
      padding-bottom: 6px;
      border-bottom: 1px solid ${t.weekdayBorder};
    }
    .sc-weekday--edit {
      cursor: pointer; border-radius: 5px; padding: 4px 2px;
      border: 1px solid transparent;
      background: none; font-family: inherit;
      color: ${t.weekdayColor}; font-size: 0.72rem; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.07em;
      transition: color 0.12s, background 0.12s, border-color 0.12s;
    }
    .sc-weekday--edit:hover {
      color: ${t.weekdayEditHoverColor};
      background: ${t.weekdayEditHoverBg};
      border-color: ${t.weekdayEditHoverBdr};
    }
    .sc-weekday--edit-on {
      color: var(--sel-color, #94a3b8) !important;
      background: color-mix(in srgb, var(--sel-color, #94a3b8) 15%, transparent) !important;
      border-color: var(--sel-color, #94a3b8) !important;
    }

    .sc-cell { position: relative; }
    .sc-cell--hovered { z-index: 30; }

    .sc-day {
      width: 100%; aspect-ratio: 1;
      border: 1.5px solid ${t.dayBorder};
      border-radius: 11px;
      background: ${t.dayBg};
      padding: 8px 7px 20px;
      cursor: pointer;
      display: flex; flex-direction: column; align-items: flex-start;
      position: relative; overflow: hidden;
      transition: border-color 0.12s, background 0.12s;
    }
    .sc-day:not(.sc-day--past):not(.sc-day--off):hover {
      border-color: ${t.dayHoverBorder};
      background: ${t.dayHoverBg};
    }
    .sc-day--off { opacity: 0.15; pointer-events: none; }

    .sc-day--past {
      opacity: 0.3;
      background: ${t.dayPastBg} !important;
      border-color: ${t.dayPastBorder} !important;
      cursor: default; pointer-events: none;
    }
    .sc-day--past::after { background: ${t.dayPastAfterBg} !important; }
    .sc-day--past .sc-day-num { color: ${t.dayPastNumColor}; }
    .sc-day--past .sc-day-badge { color: ${t.dayPastBadgeColor} !important; }

    .sc-day::after {
      content: '';
      position: absolute;
      bottom: 0; left: 0; right: 0;
      height: 3px;
      background: var(--heat-border, ${t.heatBarDefault});
      border-radius: 0 0 9px 9px;
    }

    .sc-day-num {
      font-size: 0.92rem; font-weight: 700; line-height: 1;
      color: ${t.dayNumColor};
    }
    .sc-today-pip {
      display: inline-flex; align-items: center; justify-content: center;
      width: 24px; height: 24px; border-radius: 50%;
      background: ${t.todayPipBg}; color: ${t.todayPipColor};
      font-size: 0.75rem; font-weight: 700;
    }

    .sc-day-badge {
      position: absolute; bottom: 5px; left: 7px;
      font-size: 0.56rem; font-weight: 700;
      letter-spacing: 0.01em; line-height: 1;
    }

    .sc-day-chips {
      width: 100%; margin-top: 4px;
      display: flex; flex-direction: column; gap: 2px;
      flex: 1; min-height: 0; overflow: hidden;
    }

    .sc-appt-chip {
      width: 100%; font-size: 0.5625rem; line-height: 1.35;
      padding: 2px 4px; border-radius: 3px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      font-variant-numeric: tabular-nums; font-weight: 500; flex-shrink: 0;
    }

    .sc-more-label {
      font-size: 0.5625rem; color: ${t.moreLabelColor};
      line-height: 1.3; padding-left: 2px; margin-top: 1px; flex-shrink: 0;
    }

    .sc-hover-popup {
      position: absolute; left: 0; top: 0;
      min-width: 100%;
      background: ${t.hoverPopupBg};
      border: 1.5px solid ${t.hoverPopupBorder};
      border-radius: 11px; padding: 8px 7px 9px; z-index: 50;
      display: flex; flex-direction: column; gap: 2px;
      pointer-events: none; opacity: 0;
      transform: scale(0.94); transform-origin: top left;
      transition: opacity 0.12s ease, transform 0.12s ease;
    }
    .sc-cell--hovered .sc-hover-popup { opacity: 1; transform: scale(1); }
    .sc-hover-popup .sc-day-num-small {
      font-size: 0.72rem; font-weight: 700;
      color: ${t.hoverPopupNumColor}; margin-bottom: 3px;
    }
    .sc-hover-popup .sc-appt-chip { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    .sc-day-panel {
      position: fixed; left: 8px; right: 8px;
      top: calc(50vh - ${PANEL_HEIGHT / 2}px);
      height: ${PANEL_HEIGHT}px;
      background: ${t.dayPanelBg};
      border: 1px solid ${t.dayPanelBorder};
      border-radius: 14px; z-index: 40;
      display: flex; flex-direction: column; overflow: hidden;
    }
    @media (min-width: 1024px) {
      .sc-day-panel { left: calc(16rem + 10px); right: 10px; }
    }

    .sc-panel-head {
      flex-shrink: 0; height: 52px;
      background: ${t.panelHeadBg}; color: ${t.panelHeadColor};
      display: flex; align-items: center; padding: 0 20px; gap: 12px;
    }
    .sc-panel-back {
      display: flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; flex-shrink: 0;
      border: 1px solid ${t.panelBackBorder}; border-radius: 7px;
      background: ${t.panelBackBg}; color: ${t.panelBackColor};
      cursor: pointer; padding: 0;
    }
    .sc-panel-back:hover { background: ${t.panelBackHoverBg}; color: ${t.panelBackHoverColor}; }
    .sc-panel-title {
      font-size: 0.84rem; font-weight: 700;
      text-transform: capitalize; letter-spacing: -0.01em; flex: 1;
    }
    .sc-panel-meta { font-size: 0.7rem; color: ${t.panelMetaColor}; white-space: nowrap; }

    .sc-timeline { flex: 1; display: flex; overflow: hidden; }

    .sc-hour-col {
      flex: 1; display: flex; flex-direction: column;
      border-right: 1px solid ${t.hourColBorder}; min-width: 0;
    }
    .sc-hour-col:last-child { border-right: none; }

    .sc-hour-label {
      flex-shrink: 0; height: 32px;
      display: flex; align-items: center; justify-content: center;
      font-size: 0.68rem; font-weight: 700; color: ${t.hourLabelColor};
      background: ${t.hourLabelBg};
      border-bottom: 1px solid ${t.hourLabelBorder};
      font-variant-numeric: tabular-nums; letter-spacing: 0.01em;
    }

    .sc-hour-body {
      flex: 1; padding: 6px 5px 5px;
      display: flex; flex-direction: column; gap: 4px;
      overflow: hidden; min-height: 100px;
    }
    .sc-hour-track {
      position: relative; flex: 1; min-height: 88px;
      border-radius: 6px; background: ${t.hourTrackBg};
    }

    .sc-appt-block {
      position: absolute; left: 3px; right: 3px;
      border: 1px solid ${t.apptBlockBorder};
      border-radius: 6px; background: ${t.apptBlockBg};
      overflow: hidden; z-index: 2;
      display: flex; flex-direction: column;
      transition: top 0.18s ease, height 0.18s ease;
    }
    .sc-appt-block--continued {
      border-color: ${t.apptContBorder};
      background: ${t.apptContBg};
      pointer-events: none;
    }
    .sc-appt-block--readonly {
      border-color: ${t.apptRoBorder};
      background: ${t.apptRoBg};
    }
    .sc-appt-block--readonly .sc-appt-top-bar { background: ${t.apptRoBarBg}; }
    .sc-appt-block--readonly .sc-appt-name,
    .sc-appt-block--readonly .sc-appt-svc,
    .sc-appt-block--readonly .sc-appt-plate,
    .sc-appt-block--readonly .sc-appt-time-lbl { color: ${t.apptRoTextColor}; }
    .sc-appt-continue-fill { flex: 1; min-height: 4px; }
    .sc-appt-top-bar { height: 3px; background: #ef4444; }
    .sc-appt-body { padding: 6px 7px 5px; display: flex; flex-direction: column; gap: 3px; }
    .sc-appt-time-lbl {
      font-size: 0.63rem; font-weight: 700; color: ${t.apptTimeLblColor};
      font-variant-numeric: tabular-nums;
    }
    .sc-appt-name { font-size: 0.69rem; font-weight: 700; color: ${t.apptNameColor}; line-height: 1.3; word-break: break-word; }
    .sc-appt-svc  { font-size: 0.62rem; color: ${t.apptSvcColor}; line-height: 1.3; word-break: break-word; }
    .sc-appt-plate { font-size: 0.64rem; font-weight: 600; color: ${t.apptPlateColor}; white-space: nowrap; }
    .sc-appt-acts { display: flex; gap: 3px; margin-top: 4px; }
    .sc-appt-act {
      flex: 1; height: 22px;
      display: flex; align-items: center; justify-content: center;
      border: 1px solid ${t.apptActBorder}; border-radius: 5px;
      background: ${t.apptActBg}; color: ${t.apptActColor}; cursor: pointer; padding: 0;
    }
    .sc-appt-act:hover { background: ${t.apptActHoverBg}; color: ${t.apptActHoverColor}; }
    .sc-appt-act--del:hover { color: #ef4444; border-color: rgba(239,68,68,0.4); }

    .sc-hour-add {
      flex: 1; min-height: 36px;
      border: 1.5px dashed ${t.hourAddBorder}; border-radius: 7px;
      background: transparent;
      display: flex; align-items: center; justify-content: center;
      color: ${t.hourAddColor}; cursor: pointer; padding: 0;
    }
    .sc-hour-add:hover { border-color: #4a9e8a; color: #4a9e8a; background: rgba(74,158,138,0.07); }

    .sc-hour-add-small {
      height: 20px; flex-shrink: 0;
      border: 1px dashed ${t.hourAddSmBorder}; border-radius: 5px;
      background: transparent;
      display: flex; align-items: center; justify-content: center;
      color: ${t.hourAddSmColor}; cursor: pointer; padding: 0;
      font-size: 0.5rem; gap: 2px; font-family: inherit;
    }
    .sc-hour-add-small:hover { border-color: ${t.hourAddSmHoverBdr}; color: ${t.hourAddSmHoverColor}; }

    .sc-modal-bg {
      position: fixed; inset: 0; z-index: 60;
      background: ${t.modalOverlay};
      display: flex; align-items: center; justify-content: center; padding: 16px;
    }
    .sc-modal {
      width: 100%; max-width: 390px;
      background: ${t.modalBg};
      border: 1px solid ${t.modalBorder}; border-radius: 13px; overflow: hidden;
    }
    .sc-modal-head {
      display: flex; align-items: center; justify-content: space-between;
      padding: 13px 18px;
      background: ${t.modalHeadBg}; color: ${t.modalHeadColor};
    }
    .sc-modal-head h3 { font-size: 0.86rem; font-weight: 700; }
    .sc-modal-x {
      background: none; border: none; color: ${t.modalXColor};
      cursor: pointer; padding: 2px; display: flex;
    }
    .sc-modal-x:hover { color: ${t.modalXHoverColor}; }
    .sc-form { padding: 14px 18px; display: flex; flex-direction: column; gap: 11px; }
    .sc-field label {
      display: block;
      font-size: 0.6rem; font-weight: 700; color: ${t.fieldLabelColor};
      text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 4px;
    }
    .sc-field input, .sc-field select, .sc-field textarea {
      width: 100%; padding: 8px 10px;
      border: 1px solid ${t.fieldBorder}; border-radius: 7px;
      font-family: inherit; font-size: 0.8rem;
      color: ${t.fieldColor}; background: ${t.fieldBg};
    }
    .sc-field input:focus, .sc-field select:focus, .sc-field textarea:focus {
      outline: none; border-color: ${t.fieldFocusBorder}; background: ${t.fieldFocusBg};
    }
    .sc-field select option { background: ${t.fieldOptionBg}; color: ${t.fieldOptionColor}; }
    .sc-duration-picks { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
    .sc-duration-pick {
      padding: 8px 4px;
      border: 1px solid ${t.durationBorder}; border-radius: 7px;
      background: ${t.durationBg}; color: ${t.durationColor};
      font-size: 0.72rem; font-weight: 600; font-family: inherit;
      cursor: pointer; text-align: center;
      transition: border-color 0.15s, background 0.15s, color 0.15s;
    }
    .sc-duration-pick:hover { border-color: ${t.durationHoverBorder}; color: ${t.durationHoverColor}; }
    .sc-duration-pick--active {
      border-color: #4a9e8a; background: rgba(74,158,138,0.18); color: #a7f3d0;
      box-shadow: 0 0 10px rgba(74,158,138,0.2);
    }
    .sc-duration-range { font-size: 0.68rem; color: ${t.durationRangeColor}; margin-top: 6px; }
    .sc-duration-range strong { color: ${t.durationRangeStrong}; font-weight: 600; font-variant-numeric: tabular-nums; }
    .sc-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .sc-form-btns { display: flex; gap: 8px; padding: 0 18px 14px; }
    .sc-btn {
      flex: 1; padding: 9px 12px; border-radius: 8px; font-family: inherit;
      font-size: 0.78rem; font-weight: 700; cursor: pointer; border: 1px solid transparent;
    }
    .sc-btn-primary { background: #4a9e8a; color: #0c0d11; }
    .sc-btn-primary:hover { background: #5ab5a0; }
    .sc-btn-ghost {
      background: ${t.btnGhostBg}; color: ${t.btnGhostColor};
      border-color: ${t.btnGhostBorder};
    }
    .sc-btn-ghost:hover { background: ${t.btnGhostHoverBg}; }

    .sc-edit-btn {
      display: flex; align-items: center; justify-content: center;
      width: 32px; height: 32px;
      border: 1px solid ${t.editBtnBorder}; border-radius: 8px;
      background: ${t.editBtnBg}; color: ${t.editBtnColor};
      cursor: pointer; padding: 0; margin-left: 4px;
      transition: background 0.12s, color 0.12s, border-color 0.12s;
    }
    .sc-edit-btn:hover { background: ${t.editBtnHoverBg}; color: ${t.editBtnHoverColor}; border-color: ${t.editBtnHoverBorder}; }
    .sc-edit-btn--active { background: ${t.editBtnHoverBg}; color: ${t.editBtnHoverColor}; border-color: ${t.editBtnHoverBorder}; }

    .sc-edit-panel {
      background: ${t.editPanelBg}; border: 1px solid ${t.editPanelBorder};
      border-radius: 10px; padding: 13px 15px 12px; margin-bottom: 14px;
    }
    .sc-edit-panel-head {
      display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
    }
    .sc-edit-panel-title {
      font-size: 0.68rem; font-weight: 700;
      color: ${t.editPanelTitleColor}; text-transform: uppercase; letter-spacing: 0.07em;
    }

    .sc-preset-list { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 10px; }
    .sc-preset-card {
      display: flex; align-items: center; gap: 7px;
      padding: 6px 10px 6px 8px; border-radius: 8px;
      border: 1.5px solid ${t.presetCardBorder};
      background: ${t.presetCardBg};
      cursor: pointer;
      transition: border-color 0.12s, background 0.12s;
      position: relative;
    }
    .sc-preset-card:hover { background: ${t.presetCardHoverBg}; border-color: ${t.presetCardHoverBdr}; }
    .sc-preset-card--selected { border-color: var(--pc, #64748b); background: ${t.presetCardHoverBg}; }
    .sc-preset-dot { width: 9px; height: 9px; border-radius: 50%; background: var(--pc, #64748b); flex-shrink: 0; }
    .sc-preset-name { font-size: 0.72rem; font-weight: 600; color: ${t.presetNameColor}; white-space: nowrap; }
    .sc-preset-meta { font-size: 0.6rem; color: ${t.presetMetaColor}; margin-left: 2px; white-space: nowrap; }
    .sc-preset-edit {
      background: none; border: none; cursor: pointer; padding: 0;
      color: ${t.presetEditColor}; display: flex; margin-left: 4px; transition: color 0.1s;
    }
    .sc-preset-edit:hover { color: ${t.presetEditHoverColor}; }
    .sc-preset-del {
      background: none; border: none; cursor: pointer; padding: 0;
      color: ${t.presetEditColor}; display: flex; margin-left: 2px; transition: color 0.1s;
    }
    .sc-preset-del:hover { color: ${t.presetDelHoverColor}; }
    .sc-preset-new {
      display: flex; align-items: center; gap: 5px;
      padding: 6px 10px; border-radius: 8px;
      border: 1.5px dashed ${t.presetNewBorder};
      background: transparent; cursor: pointer; color: ${t.presetNewColor};
      font-size: 0.72rem; font-weight: 600; font-family: inherit;
      transition: border-color 0.12s, color 0.12s;
    }
    .sc-preset-new:hover { border-color: ${t.presetNewHoverBdr}; color: ${t.presetNewHoverColor}; }

    .sc-edit-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 7px 12px; margin-bottom: 10px; }
    .sc-edit-field label {
      display: block;
      font-size: 0.57rem; font-weight: 700; color: ${t.fieldLabelColor};
      text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 3px;
    }
    .sc-edit-field input {
      width: 100%; padding: 5px 8px;
      border: 1px solid ${t.editFieldBorder}; border-radius: 6px;
      font-family: inherit; font-size: 0.76rem;
      color: ${t.editFieldColor}; background: ${t.editFieldBg};
    }
    .sc-edit-field input:focus { outline: none; border-color: var(--pc, #94a3b8); }
    .sc-color-row { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
    .sc-color-swatch {
      width: 20px; height: 20px; border-radius: 50%;
      border: 2px solid transparent; cursor: pointer;
      transition: transform 0.1s, border-color 0.1s;
    }
    .sc-color-swatch:hover { transform: scale(1.15); }
    .sc-color-swatch--active { border-color: ${t.colorSwatchActiveBdr}; transform: scale(1.15); }

    .sc-edit-hint {
      font-size: 0.65rem; color: ${t.editHintColor};
      margin-bottom: 9px; display: flex; align-items: center; gap: 6px;
    }
    .sc-edit-count {
      display: inline-flex; align-items: center; justify-content: center;
      font-size: 0.62rem; font-weight: 700;
      border-radius: 10px; padding: 1px 7px; min-width: 18px; color: #0c0d11;
    }
    .sc-edit-btns { display: flex; gap: 6px; }

    .sc-day--edit-selected {
      border-color: var(--sel-color, #94a3b8) !important;
      background: color-mix(in srgb, var(--sel-color, #94a3b8) 15%, transparent) !important;
    }
    .sc-edit-nav {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 8px; gap: 6px;
    }
    .sc-edit-nav-month {
      font-size: 0.72rem; font-weight: 700; color: ${t.editNavMonthColor};
      text-transform: capitalize; flex: 1; text-align: center;
    }
    .sc-edit-nav-btn {
      display: flex; align-items: center; justify-content: center;
      width: 26px; height: 26px; border-radius: 7px;
      border: 1px solid ${t.editNavBtnBorder};
      background: ${t.editNavBtnBg}; color: ${t.editNavBtnColor};
      cursor: pointer; padding: 0; flex-shrink: 0;
    }
    .sc-edit-nav-btn:hover { background: ${t.editNavBtnHoverBg}; color: ${t.editNavBtnHoverColor}; }

    .sc-weekday-btns {
      display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; margin-bottom: 10px;
    }
    .sc-weekday-btn {
      padding: 4px 2px; border-radius: 6px;
      border: 1px solid ${t.weekdayBtnBorder};
      background: ${t.weekdayBtnBg}; color: ${t.weekdayBtnColor};
      font-size: 0.6rem; font-weight: 700;
      cursor: pointer; font-family: inherit;
      text-align: center; text-transform: uppercase; letter-spacing: 0.03em;
      transition: background 0.1s, color 0.1s, border-color 0.1s;
    }
    .sc-weekday-btn:hover { background: ${t.weekdayBtnHoverBg}; color: ${t.weekdayBtnHoverColor}; border-color: ${t.weekdayBtnHoverBdr}; }
    .sc-weekday-btn--active {
      background: color-mix(in srgb, var(--sel-color, #94a3b8) 20%, transparent);
      border-color: var(--sel-color, #94a3b8);
      color: ${t.rootColor};
    }

    .sc-preset-indicator {
      position: absolute; top: 5px; right: 6px;
      width: 6px; height: 6px; border-radius: 50%;
    }
  `;
}

function dateKey(d) { return format(d, 'yyyy-MM-dd'); }

function isPastDay(d) {
  return isBefore(startOfDay(d), startOfDay(new Date()));
}

function abbrevService(s) {
  if (!s) return '';
  const map = {
    'Fiyat / Paket / Garanti': 'Fiyat/Garanti',
    'Genel Danışma': 'G. Danışma',
    // legacy labels from voice-agent bookings
    'Periyodik Bakım': 'Per. Bakım',
    'Mekanik Onarım':  'Mek. Onarım',
    'Kaporta & Boya':  'Kap. & Boya',
    'Lastik Değişimi': 'Lastik',
    'Elektrik & Elektronik': 'Elektrik',
    'Klima Servisi':   'Klima',
    'Muayene Öncesi Kontrol': 'Muayene',
  };
  return map[s] ?? s;
}

function serviceChipColors(serviceType, muted = false) {
  const map = {
    'Bakım':                  { bg: '#dcfce7', fg: '#166534' },
    'Arıza':                  { bg: '#ffedd5', fg: '#9a3412' },
    'Kaporta':                { bg: '#ede9fe', fg: '#5b21b6' },
    'HTA':                    { bg: '#dbeafe', fg: '#1e40af' },
    'Genel Danışma':          { bg: '#f4f4f5', fg: '#52525b' },
    'Fiyat / Paket / Garanti': { bg: '#fef9c3', fg: '#854d0e' },
    'Periyodik Bakım':        { bg: '#dcfce7', fg: '#166534' },
    'Mekanik Onarım':         { bg: '#ffedd5', fg: '#9a3412' },
    'Kaporta & Boya':         { bg: '#ede9fe', fg: '#5b21b6' },
    'Lastik Değişimi':        { bg: '#e0f2fe', fg: '#0369a1' },
    'Elektrik & Elektronik':  { bg: '#fef3c7', fg: '#92400e' },
    'Klima Servisi':          { bg: '#cffafe', fg: '#0e7490' },
    'Muayene Öncesi Kontrol': { bg: '#f3f4f6', fg: '#4b5563' },
  };
  const c = map[serviceType] ?? { bg: '#f4f4f5', fg: '#52525b' };
  if (muted) return { bg: '#ececee', fg: '#a1a1aa' };
  return c;
}

const makeEmptyForm = (defaultTime = '09:00', durationMinutes = DEFAULT_DURATION_MINUTES) => ({
  customerName: '', phone: '', plate: '',
  serviceType: SERVICE_TYPES[0], time: defaultTime, notes: '',
  durationMinutes,
});

/* ──────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────────*/
export default function ServiceCalendar({ onDaySelect } = {}) {
  const { isLight } = useTheme();
  const { settings } = useSettings();
  const { workingHours, slotDurationMinutes, maxAppointmentsPerSlot = 1 } = settings;

  /* slot times derived from settings */
  const allSlots = useMemo(
    () => generateSlotTimes(workingHours, slotDurationMinutes),
    [workingHours, slotDurationMinutes],
  );
  const timelineHours = useMemo(
    () => getTimelineHours(workingHours),
    [workingHours],
  );

  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarSelectedKey, setCalendarSelectedKey] = useState(null);
  const [expand, setExpand]             = useState(null);
  // expand: null | { tox, toy, sx, sy, phase:'initial'|'in'|'out' }
  const [hoveredDay, setHoveredDay]     = useState(null);
  const [modal, setModal]               = useState(null);
  const [form, setForm]                 = useState(() => makeEmptyForm(allSlots[1] ?? '09:00'));
  const [apiError, setApiError]         = useState(null);

  /* preset & per-day assignment state */
  const [presets, setPresets]                 = useState([]);          // user-created presets
  const [dayPresets, setDayPresets]           = useState({});          // dateStr → presetId
  const [editPanelOpen, setEditPanelOpen]       = useState(false);
  const [editStep, setEditStep]                 = useState('presets');   // 'presets' | 'configure'
  const [editPresetId, setEditPresetId]         = useState(null);        // chosen preset (for applying to days)
  const [editingPresetId, setEditingPresetId]   = useState(null);        // preset currently being edited
  const [newPreset, setNewPreset]               = useState(null);        // draft for new OR edited preset
  const [editSelectedDays, setEditSelectedDays] = useState(new Set());
  const [editMonth, setEditMonth]               = useState(null);        // month shown while editing

  const wrapperRef    = useRef(null);
  const hoverTimerRef = useRef(null);

  const handleDayEnter = useCallback((key) => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHoveredDay(key);
    }, 200);
  }, []);

  const handleDayLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    setHoveredDay(null);
  }, []);

  /* ── per-day config helpers ── */
  const defaultCfg = useMemo(
    () => ({ workingHours, slotDurationMinutes, maxAppointmentsPerSlot }),
    [workingHours, slotDurationMinutes, maxAppointmentsPerSlot],
  );

  /* the "Default" virtual preset (always derived from global settings) */
  const defaultPreset = useMemo(() => ({
    id: 'default', name: 'Default', color: '#64748b',
    ...defaultCfg,
  }), [defaultCfg]);

  /* all presets including Default */
  const allPresets = useMemo(() => [defaultPreset, ...presets], [defaultPreset, presets]);

  const getPresetById = useCallback(
    (id) => allPresets.find((p) => p.id === id) ?? defaultPreset,
    [allPresets, defaultPreset],
  );

  const getDayConfig = useCallback((dateStr) => {
    const pid = dayPresets[dateStr];
    return pid ? getPresetById(pid) : defaultCfg;
  }, [dayPresets, getPresetById, defaultCfg]);

  const getDayTotalSlots = useCallback((dateStr) => {
    const cfg = getDayConfig(dateStr);
    return generateSlotTimes(cfg.workingHours, cfg.slotDurationMinutes).length * (cfg.maxAppointmentsPerSlot || 1);
  }, [getDayConfig]);

  const getDayPresetColor = useCallback((dateStr) => {
    const pid = dayPresets[dateStr];
    if (!pid || pid === 'default') return null;
    return getPresetById(pid).color ?? null;
  }, [dayPresets, getPresetById]);

  /* config for the currently-open day (falls back to global) */
  const activeDayCfg = useMemo(() => {
    if (!selectedDate) return defaultCfg;
    return getDayConfig(dateKey(selectedDate));
  }, [selectedDate, getDayConfig, defaultCfg]);

  const activeDaySlots = useMemo(
    () => generateSlotTimes(activeDayCfg.workingHours, activeDayCfg.slotDurationMinutes),
    [activeDayCfg],
  );
  const activeDayTimelineHours = useMemo(
    () => getTimelineHours(activeDayCfg.workingHours),
    [activeDayCfg],
  );
  const activeDayHourDefaultTime = useMemo(() => Object.fromEntries(
    activeDayTimelineHours.map((h) => [
      h,
      activeDaySlots.find((t) => parseInt(t, 10) === h) ?? `${String(h).padStart(2, '0')}:00`,
    ]),
  ), [activeDayTimelineHours, activeDaySlots]);

  /* while edit panel is open, show editMonth so user can browse months for selection */
  const displayMonth = editPanelOpen && editMonth ? editMonth : currentMonth;

  const calendarDays = useMemo(() => {
    const s = startOfWeek(startOfMonth(displayMonth), { weekStartsOn: 1 });
    const e = endOfWeek(endOfMonth(displayMonth),   { weekStartsOn: 1 });
    return eachDayOfInterval({ start: s, end: e });
  }, [displayMonth]);

  /* ── edit-panel handlers ── */
  const openEditPanel = useCallback(() => {
    setEditStep('presets');
    setEditPresetId(null);
    setNewPreset(null);
    setEditSelectedDays(new Set());
    setEditMonth(currentMonth);
    setEditPanelOpen(true);
  }, [currentMonth]);

  const closeEditPanel = useCallback(() => {
    setEditPanelOpen(false);
    setEditStep('presets');
    setEditPresetId(null);
    setEditingPresetId(null);
    setNewPreset(null);
    setEditSelectedDays(new Set());
    setEditMonth(null);
  }, []);

  const toggleEditDay = useCallback((dateStr) => {
    setEditSelectedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr); else next.add(dateStr);
      return next;
    });
  }, []);

  const startNewPreset = useCallback(() => {
    const usedColors = presets.map((p) => p.color);
    const nextColor = PRESET_COLORS.find((c) => !usedColors.includes(c)) ?? PRESET_COLORS[presets.length % PRESET_COLORS.length];
    setNewPreset({
      name: '',
      color: nextColor,
      workingHours: { ...workingHours },
      slotDurationMinutes,
      maxAppointmentsPerSlot,
    });
    setEditStep('configure');
  }, [presets, workingHours, slotDurationMinutes, maxAppointmentsPerSlot]);

  const startEditPreset = useCallback((preset) => {
    setNewPreset({
      name: preset.name,
      color: preset.color,
      workingHours: { ...preset.workingHours },
      slotDurationMinutes: preset.slotDurationMinutes,
      maxAppointmentsPerSlot: preset.maxAppointmentsPerSlot,
    });
    setEditingPresetId(preset.id);
    setEditStep('configure');
  }, []);

  const confirmNewPreset = useCallback(() => {
    if (!newPreset || !newPreset.name.trim()) return;
    if (editingPresetId) {
      // update existing preset in-place
      setPresets((p) => p.map((x) =>
        x.id === editingPresetId
          ? { ...x, ...newPreset, name: newPreset.name.trim() }
          : x,
      ));
      setEditingPresetId(null);
    } else {
      // create new preset
      const id = `preset-${Date.now()}`;
      const created = { ...newPreset, id, name: newPreset.name.trim() };
      setPresets((p) => [...p, created]);
      setEditPresetId(id);
    }
    setEditStep('presets');
    setNewPreset(null);
  }, [newPreset, editingPresetId]);

  const deletePreset = useCallback((id) => {
    setPresets((p) => p.filter((x) => x.id !== id));
    setDayPresets((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((ds) => { if (next[ds] === id) delete next[ds]; });
      return next;
    });
    if (editPresetId === id) setEditPresetId(null);
  }, [editPresetId]);

  const toggleWeekday = useCallback((gdVal) => {
    const month = editMonth ?? currentMonth;
    const hits = calendarDays
      .filter((d) => isSameMonth(d, month) && getDay(d) === gdVal)
      .map((d) => dateKey(d));
    const allOn = hits.every((k) => editSelectedDays.has(k));
    setEditSelectedDays((prev) => {
      const next = new Set(prev);
      hits.forEach((k) => allOn ? next.delete(k) : next.add(k));
      return next;
    });
  }, [editMonth, currentMonth, calendarDays, editSelectedDays]);

  const applyPresetToDays = useCallback(() => {
    if (!editPresetId || editSelectedDays.size === 0) return;
    setDayPresets((prev) => {
      const next = { ...prev };
      editSelectedDays.forEach((ds) => {
        if (editPresetId === 'default') delete next[ds];
        else next[ds] = editPresetId;
      });
      return next;
    });
    closeEditPanel();
  }, [editPresetId, editSelectedDays, closeEditPanel]);

  /* font injection */
  useEffect(() => {
    const id = 'sc-sora';
    if (document.getElementById(id)) return undefined;
    const lk = Object.assign(document.createElement('link'), {
      id, rel: 'stylesheet', href: FONT_URL,
    });
    document.head.appendChild(lk);
    return () => document.getElementById(id)?.remove();
  }, []);

  /* load appointments from API */
  useEffect(() => {
    apiService.fetchAppointments()
      .then((data) => setAppointments(Array.isArray(data) ? data : []))
      .catch((err) => setApiError(err.message));
  }, []);

  /* derived maps */
  const dayBookedMap = useMemo(() => {
    const map = {};
    appointments.forEach((a) => {
      map[a.date] = (map[a.date] || 0) + 1;
    });
    return map;
  }, [appointments]);

  const dayApptsMap = useMemo(() => {
    const map = {};
    appointments.forEach((a) => {
      if (!map[a.date]) map[a.date] = [];
      map[a.date].push(a);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.time.localeCompare(b.time)));
    return map;
  }, [appointments]);

  const panelDayAppts = useMemo(() => {
    if (!selectedDate) return [];
    return dayApptsMap[dateKey(selectedDate)] || [];
  }, [dayApptsMap, selectedDate]);

  /* ── open / close day panel ── */
  const openDayPanel = useCallback((day, e) => {
    const wrapper = wrapperRef.current;
    const btn     = e.currentTarget;
    let tox = 0.5, toy = 0.5, sx = 0.12, sy = 0.12;

    if (btn && wrapper) {
      const bR = btn.getBoundingClientRect();
      // panel is position:fixed — use viewport coordinates
      const isLg    = window.innerWidth >= 1024;
      const panelL  = isLg ? (256 + 10) : 8;      // sidebar (16rem=256px) + gap, or 8px on mobile
      const panelW  = window.innerWidth - panelL - 10;
      const panelH  = PANEL_HEIGHT;
      const panelT  = window.innerHeight * 0.5 - panelH * 0.5;

      const cardCX  = bR.left + bR.width  / 2;
      const cardCY  = bR.top  + bR.height / 2;

      tox = (cardCX - panelL) / panelW;
      toy = (cardCY - panelT) / panelH;
      sx  = bR.width  / panelW;
      sy  = bR.height / panelH;
    }

    setSelectedDate(day);
    setExpand({ tox, toy, sx, sy, phase: 'initial' });
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        setExpand((p) => p ? { ...p, phase: 'in' } : null),
      ),
    );
  }, []);

  const handleDayClick = useCallback((day, e) => {
    const key = dateKey(day);

    if (calendarSelectedKey === key && expand === null) {
      openDayPanel(day, e);
      return;
    }

    if (expand !== null) {
      setExpand(null);
      setSelectedDate(null);
    }

    setCalendarSelectedKey(key);
    setSelectedDate(day);
    onDaySelect?.(key);
  }, [calendarSelectedKey, expand, openDayPanel, onDaySelect]);

  const closeDay = useCallback(() => {
    setExpand((p) => p ? { ...p, phase: 'out' } : null);
    setTimeout(() => { setExpand(null); setSelectedDate(null); onDaySelect?.(null); }, 280);
  }, [onDaySelect]);

  /* panel CSS transform */
  const panelStyle = expand ? {
    transformOrigin : `${(expand.tox * 100).toFixed(1)}% ${(expand.toy * 100).toFixed(1)}%`,
    transform       : expand.phase === 'in'
      ? 'scale(1)'
      : `scaleX(${expand.sx.toFixed(4)}) scaleY(${expand.sy.toFixed(4)})`,
    opacity         : expand.phase === 'in' ? 1 : 0,
    transition      : expand.phase !== 'initial'
      ? 'transform 0.30s cubic-bezier(0.32,0,0.12,1), opacity 0.22s ease'
      : 'none',
    pointerEvents   : expand.phase === 'in' ? 'auto' : 'none',
  } : {};

  const panelVisible  = expand !== null;
  const monthDimStyle = panelVisible
    ? { opacity: 0.28, pointerEvents: 'none', userSelect: 'none', transition: 'opacity 0.22s ease' }
    : { transition: 'opacity 0.22s ease' };

  const selectedIsPast = selectedDate ? isPastDay(selectedDate) : false;

  /* ── modal helpers ── */
  const openAdd  = (time, durationMinutes = DEFAULT_DURATION_MINUTES)  => {
    if (selectedIsPast) return;
    setForm({ ...makeEmptyForm(time, durationMinutes), time, durationMinutes });
    setModal({ mode: 'add', date: dateKey(selectedDate) });
  };
  const openEdit = (appt)  => {
    if (selectedIsPast) return;
    setForm({
      customerName: appt.customerName || '',
      phone: appt.customerPhone || appt.phone || '',
      plate: appt.plate || '',
      serviceType: appt.serviceType || SERVICE_TYPES[0],
      time: appt.time || '',
      durationMinutes: appt.durationMinutes || DEFAULT_DURATION_MINUTES,
      notes: appt.notes || appt.additionalComplaints || '',
    });
    setModal({ mode: 'edit', id: appt.id, date: appt.date });
  };
  const closeModal = () => {
    setModal(null);
    setForm(makeEmptyForm(allSlots[1] ?? '09:00'));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (selectedIsPast || !form.plate.trim()) return;
    const newRange = {
      start: timeStrToMinutes(form.time),
      end: timeStrToMinutes(form.time) + (form.durationMinutes || DEFAULT_DURATION_MINUTES),
    };
    const hasOverlap = appointments.some((a) => {
      if (a.date !== modal.date) return false;
      if (modal.mode === 'edit' && a.id === modal.id) return false;
      return rangesOverlap(newRange, getApptRange(a));
    });
    if (hasOverlap) {
      setApiError('Seçilen süre mevcut bir randevu ile çakışıyor.');
      return;
    }
    try {
      setApiError(null);
      if (modal.mode === 'add') {
        const saved = await apiService.createAppointment({
          date: modal.date,
          time: form.time,
          plate: form.plate,
          customerName: form.customerName,
          phone: form.phone,
          serviceType: form.serviceType,
          notes: form.notes,
        });
        setAppointments((p) => [...p, { ...saved, durationMinutes: form.durationMinutes }]);
      } else {
        const saved = await apiService.updateAppointment(modal.id, {
          customerName: form.customerName,
          plate: form.plate,
          customerPhone: form.phone,
          serviceType: form.serviceType,
          time: form.time,
          notes: form.notes,
        });
        setAppointments((p) => p.map((a) => (
          a.id === modal.id ? { ...saved, durationMinutes: form.durationMinutes } : a
        )));
      }
      closeModal();
    } catch (err) {
      setApiError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (selectedIsPast) return;
    try {
      await apiService.deleteAppointment(id);
      setAppointments((p) => p.filter((a) => a.id !== id));
    } catch (err) {
      setApiError(err.message);
    }
  };

  /* labels */
  const monthLabel    = format(displayMonth, 'MMMM yyyy', { locale: tr });
  const selectedLabel = selectedDate ? format(selectedDate, 'd MMMM yyyy, EEEE', { locale: tr }) : '';
  const bookedCount   = selectedDate ? (dayBookedMap[dateKey(selectedDate)] || 0) : 0;

  /* ──────────────────────────────────────────────────────────
     RENDER
  ─────────────────────────────────────────────────────────── */
  return (
    <div className="sc-root">
      <style>{makeCalendarStyles(isLight)}</style>

      {apiError && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 100,
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8, padding: '10px 14px',
          fontSize: '0.78rem', color: '#ef4444', maxWidth: 320,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ flex: 1 }}>{apiError}</span>
          <button type="button"
            onClick={() => setApiError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 0 }}>
            <X size={14} />
          </button>
        </div>
      )}
      <div className="sc-wrapper" ref={wrapperRef}>

        {/* ════════════════════════════════════════════
            MONTH CARD  — always in DOM, dims on expand
        ════════════════════════════════════════════ */}
        <div className="sc-month-card" style={monthDimStyle}>
          <div className="sc-month-bar">
            <span className="sc-month-label">{monthLabel}</span>
            <div className="sc-month-nav">
              <button type="button" className="sc-nav-btn"
                onClick={() => editPanelOpen
                  ? setEditMonth((m) => subMonths(m ?? currentMonth, 1))
                  : setCurrentMonth(subMonths(currentMonth, 1))}
                aria-label="Önceki ay"
              ><ChevronLeft size={15} /></button>
              <button type="button" className="sc-nav-btn"
                onClick={() => editPanelOpen
                  ? setEditMonth((m) => addMonths(m ?? currentMonth, 1))
                  : setCurrentMonth(addMonths(currentMonth, 1))}
                aria-label="Sonraki ay"
              ><ChevronRight size={15} /></button>
              <button
                type="button"
                className={`sc-edit-btn${editPanelOpen ? ' sc-edit-btn--active' : ''}`}
                onClick={() => editPanelOpen ? closeEditPanel() : openEditPanel()}
                aria-label="Gün ayarları"
                title="Seçili günlere çalışma saati uygula"
              ><SlidersHorizontal size={13} /></button>
            </div>
          </div>

          {/* ── preset panel ── */}
          {editPanelOpen && (() => {
            const chosenPreset = editPresetId ? getPresetById(editPresetId) : null;
            const selColor = chosenPreset?.color ?? '#94a3b8';
            return (
              <div className="sc-edit-panel" style={{ '--pc': selColor }}>
                <div className="sc-edit-panel-head">
                  <span className="sc-edit-panel-title">
                    {editStep === 'configure'
                      ? (editingPresetId ? 'Preset Düzenle' : 'Yeni Preset')
                      : 'Presetler'}
                  </span>
                  <button type="button"
                    style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', padding:0, display:'flex' }}
                    onClick={editStep === 'configure'
                      ? () => { setEditStep('presets'); setNewPreset(null); setEditingPresetId(null); }
                      : closeEditPanel}>
                    {editStep === 'configure' ? <ArrowLeft size={14} /> : <X size={14} />}
                  </button>
                </div>

                {editStep === 'presets' && (
                  <>
                    {/* preset cards */}
                    <div className="sc-preset-list">
                      {allPresets.map((p) => (
                        <div
                          key={p.id}
                          className={`sc-preset-card${editPresetId === p.id ? ' sc-preset-card--selected' : ''}`}
                          style={{ '--pc': p.color }}
                          onClick={() => setEditPresetId(p.id === editPresetId ? null : p.id)}
                        >
                          <span className="sc-preset-dot" />
                          <span className="sc-preset-name">{p.name}</span>
                          <span className="sc-preset-meta">
                            {p.workingHours.start}–{p.workingHours.end} · {p.slotDurationMinutes}dk
                          </span>
                          {p.id !== 'default' && (
                            <>
                              <button type="button" className="sc-preset-edit"
                                onClick={(e) => { e.stopPropagation(); startEditPreset(p); }}
                                aria-label="Düzenle"><Pencil size={9} /></button>
                              <button type="button" className="sc-preset-del"
                                onClick={(e) => { e.stopPropagation(); deletePreset(p.id); }}
                                aria-label="Sil"><X size={10} /></button>
                            </>
                          )}
                        </div>
                      ))}
                      <button type="button" className="sc-preset-new" onClick={startNewPreset}>
                        <Plus size={12} /> Yeni
                      </button>
                    </div>

                    {/* day-selection hint + weekday buttons + apply */}
                    {editPresetId && (
                      <>
                        {/* month nav for edit panel */}
                        <div className="sc-edit-nav">
                          <button type="button" className="sc-edit-nav-btn"
                            onClick={() => setEditMonth((m) => subMonths(m ?? currentMonth, 1))}>
                            <ChevronLeft size={12} />
                          </button>
                          <span className="sc-edit-nav-month">
                            {format(editMonth ?? currentMonth, 'MMMM yyyy', { locale: tr })}
                          </span>
                          <button type="button" className="sc-edit-nav-btn"
                            onClick={() => setEditMonth((m) => addMonths(m ?? currentMonth, 1))}>
                            <ChevronRight size={12} />
                          </button>
                        </div>

                        <div className="sc-edit-hint">
                          <SlidersHorizontal size={11} />
                          Günlere tıklayın
                          {editSelectedDays.size > 0 && (
                            <span className="sc-edit-count" style={{ background: selColor, color: '#0c0d11' }}>
                              {editSelectedDays.size} gün
                            </span>
                          )}
                        </div>
                        <div className="sc-edit-btns">
                          <button type="button" className="sc-btn sc-btn-ghost"
                            style={{ padding:'6px 10px', fontSize:'0.72rem' }}
                            onClick={closeEditPanel}>İptal</button>
                          <button type="button" className="sc-btn sc-btn-primary"
                            style={{ padding:'6px 10px', fontSize:'0.72rem', background: selColor, color: '#0c0d11' }}
                            onClick={applyPresetToDays}
                            disabled={editSelectedDays.size === 0}>
                            {editSelectedDays.size === 0 ? 'Gün seçin' : `${editSelectedDays.size} güne uygula`}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}

                {editStep === 'configure' && newPreset && (
                  <>
                    {/* name + color */}
                    <div className="sc-edit-fields" style={{ gridTemplateColumns: '1fr' }}>
                      <div className="sc-edit-field">
                        <label>Preset Adı</label>
                        <input value={newPreset.name} placeholder="örn. Cumartesi Kısa"
                          onChange={(e) => setNewPreset((p) => ({ ...p, name: e.target.value }))} />
                      </div>
                    </div>
                    <div className="sc-color-row">
                      {PRESET_COLORS.map((c) => (
                        <button type="button" key={c}
                          className={`sc-color-swatch${newPreset.color === c ? ' sc-color-swatch--active' : ''}`}
                          style={{ background: c }}
                          onClick={() => setNewPreset((p) => ({ ...p, color: c }))} />
                      ))}
                    </div>
                    {/* config fields */}
                    <div className="sc-edit-fields">
                      <div className="sc-edit-field">
                        <label>Başlangıç</label>
                        <input type="time" value={newPreset.workingHours.start}
                          onChange={(e) => setNewPreset((p) => ({ ...p, workingHours: { ...p.workingHours, start: e.target.value } }))} />
                      </div>
                      <div className="sc-edit-field">
                        <label>Bitiş</label>
                        <input type="time" value={newPreset.workingHours.end}
                          onChange={(e) => setNewPreset((p) => ({ ...p, workingHours: { ...p.workingHours, end: e.target.value } }))} />
                      </div>
                      <div className="sc-edit-field">
                        <label>Slot (dk)</label>
                        <input type="number" min={15} step={15} value={newPreset.slotDurationMinutes}
                          onChange={(e) => setNewPreset((p) => ({ ...p, slotDurationMinutes: Number(e.target.value) }))} />
                      </div>
                      <div className="sc-edit-field">
                        <label>Maks / slot</label>
                        <input type="number" min={1} value={newPreset.maxAppointmentsPerSlot}
                          onChange={(e) => setNewPreset((p) => ({ ...p, maxAppointmentsPerSlot: Number(e.target.value) }))} />
                      </div>
                    </div>
                    <div className="sc-edit-btns">
                      <button type="button" className="sc-btn sc-btn-ghost"
                        style={{ padding:'6px 10px', fontSize:'0.72rem' }}
                        onClick={() => { setEditStep('presets'); setNewPreset(null); }}>İptal</button>
                      <button type="button" className="sc-btn sc-btn-primary"
                        style={{ padding:'6px 10px', fontSize:'0.72rem', background: newPreset.color, color:'#0c0d11' }}
                        onClick={confirmNewPreset}
                        disabled={!newPreset.name.trim()}>Kaydet</button>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          <div className="sc-grid">
            {WEEKDAY_LABELS.map((w, i) => {
              const gdVal = WEEKDAY_GETDAY[i];
              if (editPanelOpen && editPresetId) {
                const selColor = getPresetById(editPresetId).color;
                const hits  = calendarDays
                  .filter((d) => isSameMonth(d, displayMonth) && getDay(d) === gdVal)
                  .map((d) => dateKey(d));
                const allOn = hits.length > 0 && hits.every((k) => editSelectedDays.has(k));
                return (
                  <button key={w} type="button"
                    className={`sc-weekday sc-weekday--edit${allOn ? ' sc-weekday--edit-on' : ''}`}
                    style={{ '--sel-color': selColor }}
                    onClick={() => toggleWeekday(gdVal)}>
                    {w}
                  </button>
                );
              }
              return <div key={w} className="sc-weekday">{w}</div>;
            })}
            {calendarDays.map((day) => {
              const inMonth  = isSameMonth(day, displayMonth);
              const key      = dateKey(day);
              const booked   = dayBookedMap[key] || 0;
              const dayTotal = getDayTotalSlots(key);
              const ratio    = booked / (dayTotal || 1);
              const heat     = heatColors(ratio, isLight);
              const today    = isToday(day);
              const past         = inMonth && isPastDay(day);
              const dayAppts     = dayApptsMap[key] || [];
              const isHov        = hoveredDay === key && inMonth && dayAppts.length > 0 && !editPanelOpen;
              const baseChips    = dayAppts.slice(0, DEFAULT_CHIP_COUNT);
              const hiddenCount  = dayAppts.length - DEFAULT_CHIP_COUNT;
              const isEditSelected  = editPanelOpen && editSelectedDays.has(key);
              const presetColor     = inMonth ? getDayPresetColor(key) : null;
              const chosenEditColor = editPresetId ? getPresetById(editPresetId).color : '#94a3b8';

              return (
                <div
                  key={day.toISOString()}
                  className={`sc-cell${isHov ? ' sc-cell--hovered' : ''}`}
                >
                  <button
                    type="button"
                    className={`sc-day${!inMonth ? ' sc-day--off' : ''}${past ? ' sc-day--past' : ''}${isEditSelected ? ' sc-day--edit-selected' : ''}`}
                    style={isEditSelected
                      ? { '--sel-color': chosenEditColor }
                      : inMonth && !past ? { '--heat-border': heat.border, background: heat.bg, borderColor: heat.border }
                      : {}}
                    onMouseEnter={() => inMonth && handleDayEnter(key)}
                    onMouseLeave={handleDayLeave}
                    onClick={(e) => {
                      if (!inMonth) return;
                      if (editPanelOpen) { toggleEditDay(key); return; }
                      handleDayClick(day, e);
                    }}
                  >
                    {/* preset color indicator dot */}
                    {presetColor && !isEditSelected && (
                      <span className="sc-preset-indicator" style={{ background: presetColor }} />
                    )}
                    {/* date number — top-left */}
                    <span className="sc-day-num">
                      {today
                        ? <span className="sc-today-pip">{format(day, 'd')}</span>
                        : format(day, 'd')}
                    </span>

                    {/* base chips — up to DEFAULT_CHIP_COUNT always visible */}
                    {inMonth && baseChips.length > 0 && (
                      <div className="sc-day-chips">
                        {baseChips.map((a) => {
                          const chip = serviceChipColors(a.serviceType, past);
                          return (
                            <span
                              key={a.id}
                              className="sc-appt-chip"
                              style={{ background: chip.bg, color: chip.fg }}
                            >
                              {a.time} · {a.plate} · {abbrevService(a.serviceType)}
                            </span>
                          );
                        })}
                        {hiddenCount > 0 && (
                          <span className="sc-more-label">+{hiddenCount} daha</span>
                        )}
                      </div>
                    )}

                    {/* slot badge — bottom-left */}
                    {inMonth && (
                      <span className="sc-day-badge" style={past ? {} : { color: heat.badge }}>
                        {booked}/{dayTotal}
                      </span>
                    )}
                  </button>

                  {/* hover popup — full list, auto-height, z-index above grid */}
                  {inMonth && dayAppts.length > 0 && (
                    <div className="sc-hover-popup" style={{ background: !past ? heat.bg : 'rgba(255,255,255,0.04)', borderColor: !past ? heat.border : 'rgba(255,255,255,0.1)' }}>
                      <span className="sc-day-num-small">{format(day, 'd MMM', { locale: tr })}</span>
                      {dayAppts.map((a) => {
                        const chip = serviceChipColors(a.serviceType, past);
                        return (
                          <span
                            key={a.id}
                            className="sc-appt-chip"
                            style={{ background: chip.bg, color: chip.fg }}
                          >
                            {a.time} · {a.plate} · {abbrevService(a.serviceType)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ════════════════════════════════════════════
            DAY DETAIL PANEL  — smaller overlay, animated
        ════════════════════════════════════════════ */}
        {panelVisible && (
          <div
            aria-hidden="true"
            onClick={closeDay}
            style={{
              position: 'fixed', inset: 0,
              zIndex: 39,
              cursor: 'pointer',
            }}
          />
        )}
        {panelVisible && (
          <div className="sc-day-panel" style={panelStyle} role="dialog" aria-label="Gün detayı">
            {/* navy header */}
            <div className="sc-panel-head">
              <button type="button" className="sc-panel-back" onClick={closeDay} aria-label="Geri">
                <ArrowLeft size={14} />
              </button>
              <span className="sc-panel-title">{selectedLabel}</span>
              <span className="sc-panel-meta">
                {bookedCount}/{activeDaySlots.length * (activeDayCfg.maxAppointmentsPerSlot || 1)} dolu
                {selectedIsPast && ' · salt okunur'}
                {selectedDate && dayPresets[dateKey(selectedDate)] && ` · ${getPresetById(dayPresets[dateKey(selectedDate)]).name}`}
              </span>
            </div>

            {/* horizontal hourly timeline */}
            <div className="sc-timeline">
              {activeDayTimelineHours.map((h) => {
                const startsInHour = panelDayAppts.filter(
                  (a) => parseInt(a.time.split(':')[0], 10) === h,
                );
                const defaultTime = activeDayHourDefaultTime[h];
                return (
                  <div key={h} className="sc-hour-col">
                    <div className="sc-hour-label">
                      {String(h).padStart(2, '0')}:00
                    </div>
                    <div className="sc-hour-body">
                      <div className="sc-hour-track">
                        {panelDayAppts.map((appt) => {
                          const overlap = getHourOverlap(h, getApptRange(appt));
                          if (!overlap) return null;
                          const range = getApptRange(appt);
                          const endTime = minutesToTimeStr(range.end);
                          return (
                            <div
                              key={`${appt.id}-${h}`}
                              className={`sc-appt-block${overlap.isStart ? ' sc-appt-block--start' : ' sc-appt-block--continued'}${selectedIsPast ? ' sc-appt-block--readonly' : ''}`}
                              style={{
                                top: `${overlap.topPercent}%`,
                                height: `${overlap.heightPercent}%`,
                              }}
                            >
                              <div className="sc-appt-top-bar" />
                              {overlap.isStart ? (
                                <div className="sc-appt-body">
                                  <div className="sc-appt-time-lbl">
                                    {appt.time} – {endTime}
                                    {appt.durationMinutes ? ` · ${appt.durationMinutes} dk` : ''}
                                  </div>
                                  <div className="sc-appt-name">{appt.customerName}</div>
                                  <div className="sc-appt-svc">{abbrevService(appt.serviceType)}</div>
                                  <div className="sc-appt-plate">{appt.plate}</div>
                                  {!selectedIsPast && (
                                    <div className="sc-appt-acts">
                                      <button type="button" className="sc-appt-act"
                                        onClick={() => openEdit(appt)} aria-label="Düzenle">
                                        <Pencil size={9} />
                                      </button>
                                      <button type="button" className="sc-appt-act sc-appt-act--del"
                                        onClick={() => handleDelete(appt.id)} aria-label="Sil">
                                        <Trash2 size={9} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="sc-appt-continue-fill" aria-hidden="true" />
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* add button — today and future only */}
                      {!selectedIsPast && (startsInHour.length === 0 ? (
                        <button type="button" className="sc-hour-add"
                          onClick={() => openAdd(defaultTime)}
                          aria-label={`${h}:00 randevu ekle`}>
                          <Plus size={15} />
                        </button>
                      ) : (
                        <button type="button" className="sc-hour-add-small"
                          onClick={() => openAdd(defaultTime)}
                          aria-label="Ekle">
                          <Plus size={10} /> ekle
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════
          MODAL
      ════════════════════════════════════════════ */}
      {modal && (
        <div className="sc-modal-bg" onClick={closeModal} role="presentation">
          <div className="sc-modal" onClick={(e) => e.stopPropagation()}
            role="dialog" aria-label={modal.mode === 'add' ? 'Randevu ekle' : 'Randevu düzenle'}>
            <div className="sc-modal-head">
              <h3>{modal.mode === 'add' ? 'Yeni Randevu' : 'Randevuyu Düzenle'}</h3>
              <button type="button" className="sc-modal-x" onClick={closeModal} aria-label="Kapat">
                <X size={16} />
              </button>
            </div>
            <form className="sc-form" onSubmit={handleSave}>
              <div className="sc-field">
                <label htmlFor="sc-name">Müşteri Adı</label>
                <input id="sc-name" value={form.customerName} required
                  onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))} />
              </div>
              <div className="sc-row2">
                <div className="sc-field">
                  <label htmlFor="sc-phone">Telefon</label>
                  <input id="sc-phone" value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="sc-field">
                  <label htmlFor="sc-plate">Plaka</label>
                  <input id="sc-plate" value={form.plate} required
                    onChange={(e) => setForm((f) => ({ ...f, plate: e.target.value }))} />
                </div>
              </div>
              <div className="sc-field">
                <label htmlFor="sc-svc">Hizmet Türü</label>
                <select id="sc-svc" value={form.serviceType}
                  onChange={(e) => setForm((f) => ({ ...f, serviceType: e.target.value }))}>
                  {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="sc-field">
                <label htmlFor="sc-time">Başlangıç</label>
                <select id="sc-time" value={form.time}
                  onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}>
                  {activeDaySlots.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="sc-field">
                <label>Süre</label>
                <div className="sc-duration-picks" role="group" aria-label="Randevu süresi">
                  {DURATION_OPTIONS.map((d) => (
                    <button
                      type="button"
                      key={d}
                      className={`sc-duration-pick${form.durationMinutes === d ? ' sc-duration-pick--active' : ''}`}
                      onClick={() => setForm((f) => ({ ...f, durationMinutes: d }))}
                    >
                      {d} dk
                    </button>
                  ))}
                </div>
                <div className="sc-duration-range">
                  Bitiş:{' '}
                  <strong>
                    {minutesToTimeStr(
                      timeStrToMinutes(form.time) + (form.durationMinutes || DEFAULT_DURATION_MINUTES),
                    )}
                  </strong>
                </div>
              </div>
              <div className="sc-field">
                <label htmlFor="sc-notes">Notlar</label>
                <textarea id="sc-notes" rows={2} value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="sc-form-btns">
                <button type="button" className="sc-btn sc-btn-ghost" onClick={closeModal}>İptal</button>
                <button type="submit" className="sc-btn sc-btn-primary">
                  {modal.mode === 'add' ? 'Ekle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
